import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical, QrCode, Smartphone, Landmark, Copy, CheckCircle2, Loader2, Timer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, rupiah } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PageLoader } from "@/components/layout/RequireAuth";

export const SimulationBadge = () => (
  <div className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm font-bold text-amber-900" data-testid="simulation-badge">
    <FlaskConical className="h-4 w-4" /> MODE SIMULASI – tidak ada uang sungguhan
  </div>
);

const FakeQR = ({ text, size = 29 }) => {
  let h = 2166136261;
  const bits = [];
  for (let i = 0; i < size * size; i++) {
    h ^= text.charCodeAt(i % text.length) + i;
    h = Math.imul(h, 16777619) >>> 0;
    bits.push(h % 7 < 3);
  }
  const finder = (x, y) => [[0, 0], [size - 7, 0], [0, size - 7]].some(([fx, fy]) => x >= fx && x < fx + 7 && y >= fy && y < fy + 7);
  const finderOn = (x, y) => {
    const [fx, fy] = [[0, 0], [size - 7, 0], [0, size - 7]].find(([a, b]) => x >= a && x < a + 7 && y >= b && y < b + 7);
    const dx = x - fx, dy = y - fy;
    return dx === 0 || dy === 0 || dx === 6 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4);
  };
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="h-52 w-52 rounded-xl bg-white p-2" data-testid="qris-image" shapeRendering="crispEdges">
      {Array.from({ length: size * size }).map((_, i) => {
        const x = i % size, y = Math.floor(i / size);
        const on = finder(x, y) ? finderOn(x, y) : bits[i];
        return on ? <rect key={i} x={x} y={y} width="1" height="1" fill="#1c1917" /> : null;
      })}
    </svg>
  );
};

const GROUP_META = { qris: ["QRIS", QrCode], ewallet: ["E-Wallet", Smartphone], va: ["Virtual Account", Landmark] };

const MethodPicker = ({ methods, value, onChange }) => (
  <div className="space-y-5" data-testid="method-picker">
    {Object.entries(GROUP_META).map(([g, [label, Icon]]) => (
      <div key={g}>
        <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-stone-500"><Icon className="h-4 w-4" /> {label}</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {methods.filter((m) => m.group === g).map((m) => (
            <button key={m.key} type="button" onClick={() => onChange(m.key)} data-testid={`method-${m.key}`}
              className={cn("rounded-xl border px-4 py-3 text-left text-sm font-medium transition-[border-color,background-color]",
                value === m.key ? "border-teal-600 bg-teal-50 text-teal-900" : "border-stone-200 bg-white hover:border-stone-300")}>
              {m.label}
            </button>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Countdown = ({ until }) => {
  const [left, setLeft] = useState(new Date(until) - Date.now());
  useEffect(() => {
    const id = setInterval(() => setLeft(new Date(until) - Date.now()), 1000);
    return () => clearInterval(id);
  }, [until]);
  const s = Math.max(0, Math.floor(left / 1000));
  const pad = (n) => String(n).padStart(2, "0");
  return <span className="font-mono font-bold" data-testid="payment-countdown">{pad(Math.floor(s / 3600))}:{pad(Math.floor((s % 3600) / 60))}:{pad(s % 60)}</span>;
};

const Instructions = ({ p, onSimulate, busy }) => {
  const ins = p.instructions;
  const copy = () => { navigator.clipboard?.writeText(ins.va_number); toast.success("Nomor VA disalin"); };
  return (
    <div className="space-y-5" data-testid="payment-instructions">
      <div className="flex items-center justify-between rounded-xl bg-stone-50 px-4 py-3 text-sm">
        <span className="font-semibold text-stone-800">{p.method_label}</span>
        <span className="flex items-center gap-1.5 text-stone-600"><Timer className="h-4 w-4" /> Bayar dalam <Countdown until={p.expires_at} /></span>
      </div>
      {ins.type === "qris" && <div className="flex justify-center rounded-2xl border border-stone-200 bg-stone-50 p-4"><FakeQR text={ins.qr_string} /></div>}
      {ins.type === "va" && (
        <div className="rounded-2xl border border-stone-200 p-5">
          <p className="text-xs text-stone-500">Nomor Virtual Account {ins.bank}</p>
          <div className="mt-1 flex items-center justify-between gap-3">
            <p className="font-mono text-2xl font-bold tracking-wider text-stone-900" data-testid="va-number">{ins.va_number}</p>
            <Button variant="outline" size="sm" onClick={copy} className="rounded-lg" data-testid="copy-va-button"><Copy className="mr-1 h-3.5 w-3.5" /> Salin</Button>
          </div>
        </div>
      )}
      <ol className="list-decimal space-y-1.5 pl-5 text-sm text-stone-600" data-testid="payment-steps">
        {ins.steps.map((s) => <li key={s}>{s}</li>)}
      </ol>
      <Button onClick={onSimulate} disabled={busy} className="h-12 w-full rounded-xl bg-amber-500 font-bold text-stone-900 hover:bg-amber-400" data-testid="simulate-payment-button">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Simulasikan Pembayaran Berhasil
      </Button>
    </div>
  );
};

export default function Checkout() {
  const [params, setParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const orderId = params.get("pesanan");
  const creditKey = params.get("kredit");
  const paymentId = params.get("pembayaran");
  const [target, setTarget] = useState(null);
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState("qris");
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/payments/methods").then((r) => setMethods(r.data));
    if (orderId) api.get(`/orders/${orderId}`).then((r) => setTarget({ title: r.data.title, amount: r.data.price, status: r.data.status }));
    if (creditKey) api.get("/settings/public").then((r) => {
      const pkg = r.data.credit_packages.find((c) => c.key === creditKey);
      if (pkg) setTarget({ title: `${pkg.credits} kredit`, amount: pkg.price, status: "menunggu_pembayaran" });
    });
  }, [orderId, creditKey]);

  const loadPayment = useCallback(() => paymentId && api.get(`/payments/${paymentId}`).then((r) => setPayment(r.data)).catch(() => setPayment(null)), [paymentId]);
  useEffect(() => { loadPayment(); }, [loadPayment]);

  const create = async () => {
    setBusy(true);
    try {
      const body = orderId ? { purpose: "order", order_id: orderId, method } : { purpose: "credits", package_key: creditKey, method };
      const { data } = await api.post("/payments", body);
      setPayment(data);
      setParams({ ...(orderId ? { pesanan: orderId } : { kredit: creditKey }), pembayaran: data.payment_id });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const simulate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/${payment.payment_id}/simulate-success`);
      setPayment(data);
      if (data.status === "paid") { toast.success("Pembayaran berhasil (simulasi)"); refreshUser(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!target) return <PageLoader />;
  const backLink = orderId ? `/pesanan/${orderId}` : "/dasbor/kredit";
  const paid = payment?.status === "paid";

  return (
    <div className="container-page max-w-3xl py-10 lg:py-14" data-testid="checkout-page">
      <SimulationBadge />
      <h1 className="mt-6 text-3xl font-bold tracking-tight text-stone-900 sm:text-4xl">Pembayaran</h1>
      <div className="card-soft mt-6 flex items-center justify-between gap-4 p-6" data-testid="checkout-summary">
        <div><p className="text-sm text-stone-500">{orderId ? "Pesanan" : "Pembelian kredit"}</p><p className="font-bold text-stone-900">{target.title}</p></div>
        <p className="text-2xl font-extrabold text-stone-900" data-testid="checkout-amount">{rupiah(target.amount)}</p>
      </div>
      <div className="card-soft mt-6 p-6 sm:p-8">
        {paid ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center" data-testid="payment-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-600" />
            <p className="text-xl font-bold text-stone-900">Pembayaran berhasil</p>
            <p className="text-sm text-stone-500">{orderId ? "Dana ditahan Tuntas hingga Anda mengonfirmasi pekerjaan selesai." : "Kredit sudah ditambahkan ke saldo Anda."}</p>
            <Button asChild className="mt-2 rounded-xl bg-teal-700 hover:bg-teal-800" data-testid="payment-success-continue"><Link to={backLink}>Lanjut</Link></Button>
          </div>
        ) : target.status !== "menunggu_pembayaran" ? (
          <p className="text-sm text-stone-600" data-testid="checkout-not-payable">Pesanan ini tidak menunggu pembayaran. <Link to={backLink} className="font-semibold text-teal-700">Lihat pesanan</Link></p>
        ) : payment?.status === "pending" ? (
          <>
            <Instructions p={payment} onSimulate={simulate} busy={busy} />
            <button onClick={() => { setPayment(null); setParams(orderId ? { pesanan: orderId } : { kredit: creditKey }); }} className="mt-4 text-sm font-semibold text-stone-500 hover:text-stone-800" data-testid="change-method-button">Ganti metode pembayaran</button>
          </>
        ) : (
          <>
            {payment && <p className="mb-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-800" data-testid="payment-inactive">Pembayaran sebelumnya {payment.status}. Silakan pilih metode lagi.</p>}
            <MethodPicker methods={methods} value={method} onChange={setMethod} />
            <Button onClick={create} disabled={busy} className="mt-6 h-11 w-full rounded-xl bg-teal-700 font-semibold hover:bg-teal-800" data-testid="create-payment-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Bayar {rupiah(target.amount)}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
